import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    // The initial product view intentionally keeps the product's main media.
    // After a variant is chosen, select its matching gallery slide directly
    // instead of relying on the server-rendered media order.
    const variantId = event.detail.resource?.id;
    const variantMediaMapElement = newMediaGallery.querySelector('[data-variant-media-map]');
    let variantMediaMap = {};

    try {
      variantMediaMap = JSON.parse(variantMediaMapElement?.textContent || '{}');
    } catch {
      // Fall back to the event payload if the map is malformed.
    }

    const selectedMediaSrc = variantMediaMap[variantId] || event.detail.resource?.featured_media?.preview_image?.src;
    if (!selectedMediaSrc) return;

    const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
    const renderedSlideshow = newMediaGallery.querySelector('slideshow-component');

    // After an Items choice on mobile, keep the chosen image in view instead
    // of resuming the theme's auto-rotate timer.
    if (isMobileViewport) {
      renderedSlideshow?.removeAttribute('autoplay');
      renderedSlideshow?.pause?.();
    }

    const selectVariantMedia = () => {
      // The ref can be unavailable immediately after an HTML replacement,
      // while the child custom element is already present in the DOM.
      const slideshow = newMediaGallery.slideshow || newMediaGallery.querySelector('slideshow-component');
      const normalizeMediaSrc = (src) => {
        const extractFileName = (path) => {
          const filesMarker = '/files/';
          const filesIndex = path.indexOf(filesMarker);
          const normalizedPath = filesIndex === -1 ? path : path.slice(filesIndex + filesMarker.length);
          return normalizedPath.replace(/^\//, '');
        };

        try {
          return extractFileName(new URL(src, window.location.origin).pathname);
        } catch {
          return extractFileName(src.split('?')[0]);
        }
      };
      const selectedMediaPath = normalizeMediaSrc(selectedMediaSrc);
      const slides = Array.from(slideshow?.refs?.slides ?? newMediaGallery.querySelectorAll('slideshow-slide') ?? []);
      const matchingSlide = slides.find(
        (slide) => normalizeMediaSrc(slide.dataset.mediaSrc || '') === selectedMediaPath
      );
      const slideId = matchingSlide?.getAttribute('slide-id');

      if (slideId) {
        if (typeof slideshow?.select === 'function') {
          slideshow.select({ id: slideId }, undefined, { animate: false });
        } else {
          // Fallback for galleries whose slideshow module has not upgraded yet.
          // Do not use scrollIntoView here: on iOS it also scrolls the page
          // vertically, leaving the product media partially off-screen.
          const scroller = newMediaGallery.querySelector('[ref="scroller"]');
          scroller?.scrollTo({ left: matchingSlide?.offsetLeft ?? 0, behavior: 'instant' });
          slides.forEach((slide) => slide.setAttribute('aria-hidden', slide === matchingSlide ? 'false' : 'true'));
        }
      }
    };

    // The replacement gallery connects after it is inserted into the DOM.
    // Wait two frames, then retry once for slow custom-element upgrades.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        selectVariantMedia();
        setTimeout(selectVariantMedia, 100);
      });
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
